import htmlReactParser, { DOMNode, domToReact } from 'html-react-parser';
import React, { useMemo } from 'react';

export type DomNodeDefinition = { id?: string; name: string; attribs: { [name: string]: string }; children: DOMNode[] };

export type DecorateHtmlRules = { [tagName: string]: (node: DomNodeDefinition, children: React.ReactNode) => React.ReactNode };

export type StaticMarkdownRendererProps = {
  markdownHtml: string;
  decoratorRules?: DecorateHtmlRules;
};

export const StaticMarkdownRenderer: React.FC<StaticMarkdownRendererProps> = React.memo((props) => {
  const { markdownHtml, decoratorRules } = props;
  const parsedHtmlContent = useMemo(
    () =>
      htmlReactParser(markdownHtml, {
        replace: (rawNode) => processDOMNode(rawNode, decoratorRules),
      }),
    [markdownHtml, decoratorRules],
  );
  return <React.Fragment>{parsedHtmlContent}</React.Fragment>;
});
StaticMarkdownRenderer.displayName = 'StaticMarkdownRenderer';

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

const processDOMNode = (node: DOMNode, decoratorRules?: DecorateHtmlRules): DOMNode => {
  const { children, name } = node as DomNodeDefinition;
  const childrenWithDecorators = (children || []).map((child) => {
    const castedChild = child as DomNodeDefinition;
    const childName = castedChild.name;
    if (!!decoratorRules && decoratorRules.hasOwnProperty(childName)) {
      return decoratorRules[childName](castedChild, domToReact(castedChild.children));
    }
    return domToReact([child]);
  });
  if (!!decoratorRules && decoratorRules.hasOwnProperty(name)) {
    return decoratorRules[name](node as DomNodeDefinition, childrenWithDecorators as any) as unknown as DOMNode;
  }
  return node;
};
